import { useState, useEffect, useContext } from "react";
import { isMobile } from "react-device-detect";
import dayjs from "dayjs";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { DatabaseContext, UserContext } from "../../context";
import UserAvatar from "../management/UserAvatar";
import firebase from "firebase";

export default function ConversationMessage(props) {
    const [time, setTime] = useState(Date.now());
    const [dateString, setDateString] = useState("");
    const { users } = useContext(DatabaseContext);
    const [user, setUser] = useState();
    const [isHovering, setIsHovering] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const { currentUserData } = useContext(UserContext);

    useEffect(() => {
        const interval = setInterval(() => setTime(Date.now()), 5000);
        let commentDate = dayjs(props.message.date);
        let now = dayjs();

        if (!props.message.user.fullName) {
            let userId = props.message.user;
            setUser(users.find((availableUser) => availableUser.key === userId));
        }

        let diffs = [
            { interval: "years", value: now.diff(commentDate, "years") },
            { interval: "months", value: now.diff(commentDate, "months") },
            { interval: "weeks", value: now.diff(commentDate, "weeks") },
            { interval: "days", value: now.diff(commentDate, "days") },
            { interval: "hours", value: now.diff(commentDate, "hours") },
            { interval: "minutes", value: now.diff(commentDate, "minutes") },
        ].filter((diff) => diff.value > 0);

        if (diffs.length === 0) {
            props.message.dateString = "a moment ago";
            setDateString(props.message.dateString);
        } else {
            let diff = diffs[0];

            if (diff.value === 1) {
                props.message.dateString =
                    diff.value + " " + diff.interval.replace("s", "") + " ago ";
                setDateString(props.message.dateString);
            } else {
                props.message.dateString =
                    diff.value + " " + diff.interval + " ago ";
                setDateString(props.message.dateString);
            }
        }

        return () => {
            clearInterval(interval);
        };
    }, [props.message, time, users]);

    function onMouseEnter() {
        if (currentUserData && props.message.user === currentUserData.key) {
            setIsHovering(true);
        }
    }

    function onMouseLeave() {
        setIsHovering(false);
    }

    const handleMenuClick = (event) => {
        setMenuOpen(true);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleDeleteButtonPress = () => {
        let messageRef = firebase
            .database()
            .ref("conversations")
            .child(props.conversation.key)
            .child("messages")
            .child(props.message.key);

        messageRef.update({ deleted: true });

        handleMenuClose();
    };

    const isOwnMessage =
        currentUserData && props.message.user === currentUserData.key;

    return (
        <>
            <Box
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: isOwnMessage ? "flex-end" : "flex-start",
                    gap: 1,
                    mb: 1.75,
                }}
            >
                {!isOwnMessage && (
                    <Box sx={{ flexShrink: 0, mb: 0.25, display: "flex" }}>
                        <UserAvatar userOrHub={user} path="users" size="small" />
                    </Box>
                )}

                <Box
                    sx={{
                        maxWidth: { xs: "82%", sm: "72%", md: "68%" },
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: isOwnMessage ? "flex-end" : "flex-start",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 0.5,
                            mb: 0.45,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 800, color: "text.primary" }}
                        >
                            {isOwnMessage ? "You" : props.message.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {dateString}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            position: "relative",
                            px: 1.6,
                            py: 1.15,
                            borderRadius: isOwnMessage
                                ? "18px 18px 5px 18px"
                                : "18px 18px 18px 5px",
                            color: isOwnMessage ? "common.white" : "text.primary",
                            backgroundColor: isOwnMessage
                                ? "primary.main"
                                : "background.paper",
                            border: isOwnMessage ? "none" : "1px solid",
                            borderColor: "divider",
                            boxShadow: isOwnMessage
                                ? "0 5px 14px rgba(0, 120, 48, 0.14)"
                                : "0 4px 12px rgba(20, 35, 25, 0.055)",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                        >
                            {props.message.message}
                        </Typography>
                    </Box>
                </Box>

                {!props.hideMenu && (
                    <IconButton
                        aria-label="Message options"
                        hidden={!isHovering && !menuOpen && !isMobile}
                        onClick={handleMenuClick}
                        size="small"
                        sx={{ mb: 0.4, color: "text.secondary" }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            <Menu
                id="message-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                slotProps={{ paper: { sx: { borderRadius: 2 } } }}
            >
                <MenuItem onClick={handleDeleteButtonPress} sx={{ gap: 1 }}>
                    <DeleteOutlineIcon fontSize="small" />
                    Delete message
                </MenuItem>
            </Menu>
        </>
    );
}
